/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ElkPoint } from '../networkPolicy/elkLayout';
import type { ElkRect } from '../networkPolicy/elkLayout';
import { KgEdge, KgNode } from './types';
import { edgeId,
  RelationFamily,
  RepoGroup,
  relationFamily,
  symbolKind,
} from './repoGraphModel';
import { LINE, NodeSize, fitText } from './textMeasure';

// GPU-path renderer for the symbol graph (overdrive direction C): one
// DPR-aware canvas, dirty-flag rAF redraws, viewport culling and zoom-based
// LOD. ELK still owns placement and routes — this only replaces the PAINT
// layer (2k+ absolutely-positioned divs + SVG polylines) so pan/zoom stay at
// 60fps at any scale. The canvas is presentation-only: semantics live in the
// DOM around it (toolbar, legend, detail panel) and the table view remains
// the screen-reader path.

export type CanvasPalette = {
  canvasBg: string;
  nodeBg: string;
  nodeBorder: string;
  groupBg: string;
  groupBorder: string;
  headerText: string;
  labelText: string;
  ring: string;
  kind: Record<string, string>; // symbolKind -> color
  family: Record<RelationFamily, { stroke: string; dash: number[]; width: number }>;
};

export type CameraApi = {
  getZoom: () => number;
  zoomTo: (z: number) => void;
  zoomBy: (factor: number) => void;
  fit: () => void;
  centerOn: (id: string, minZoom?: number) => void;
};

type Layout = {
  positions: Map<string, ElkPoint>;
  groupBounds: Map<string, ElkRect>;
  routes: Map<string, ElkPoint[]>;
  width: number;
  height: number;
};

const ZOOM_MIN = 0.02;
const ZOOM_MAX = 4;
const CAPTION_PX = 11;

// roundRect is missing on Safari < 16 — fall back to a plain rect rather
// than throwing on the first frame (which would look like a blank canvas).
const pathRoundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  if (typeof (ctx as any).roundRect === 'function') {
    (ctx as any).roundRect(x, y, w, h, r);
  } else {
    ctx.rect(x, y, w, h);
  }
};
// LOD gates: below these zooms the text is sub-legible — skip the paint.
const LOD_LABEL = 0.35;
const LOD_HEADER = 0.2;

const nodeTitle = (n: KgNode, degree: number) => {
  const loc = n.source_file
    ? ` · ${n.source_file}${n.source_location ? `:${n.source_location}` : ''}`
    : '';
  return `${n.label ?? n.id} · ${n._dir ? 'directory' : symbolKind(n)}${loc} · ${degree} connection${degree === 1 ? '' : 's'}`;
};

export const RepoGraphCanvas = (props: {
  layout: Layout;
  leafNodes: KgNode[];
  viewGroups: RepoGroup[];
  drawnEdges: KgEdge[];
  nodeSizes: Map<string, NodeSize>;
  degree: Map<string, number>;
  bucketFont: (id: string) => { px: number; weight: number };
  hiddenFamilies: Set<RelationFamily>;
  matches: Set<string> | null;
  groupMatches: Set<string> | null;
  activeIds: (string | null)[];
  fontFamily: string;
  palette: CanvasPalette;
  prefersReducedMotion: boolean;
  height: string;
  regionLabel: string;
  onSelect: (id: string | null) => void;
  onZoomChange: (z: number) => void;
  // First draw exception of a streak, so a paint failure is never silent.
  onDrawError?: (message: string) => void;
  cameraRef: React.MutableRefObject<CameraApi | null>;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  // Camera in world coords: (x, y) = world point at the viewport's top-left.
  const cam = useRef({ x: -20, y: -20, z: 1, fitted: false });
  const dirty = useRef(true);
  const drawErrored = useRef(false);
  const raf = useRef(0);
  const hoverId = useRef<string | null>(null);
  const anim = useRef(0);

  const p = props;
  const propsRef = useRef(p);
  propsRef.current = p;

  // Precompute edge polylines + bboxes once per layout for culling.
  const edgeGeom = useMemo(() => {
    const out: { pts: ElkPoint[]; bb: ElkRect; fam: RelationFamily; e: KgEdge }[] = [];
    p.drawnEdges.forEach((e, i) => {
      const pts = p.layout.routes.get(edgeId(e, i));
      if (!pts || pts.length < 2) return;
      let x1 = Infinity;
      let y1 = Infinity;
      let x2 = -Infinity;
      let y2 = -Infinity;
      for (const pt of pts) {
        x1 = Math.min(x1, pt.x); y1 = Math.min(y1, pt.y);
        x2 = Math.max(x2, pt.x); y2 = Math.max(y2, pt.y);
      }
      out.push({
        pts,
        bb: { x: x1, y: y1, width: x2 - x1, height: y2 - y1 },
        fam: relationFamily(e.relation),
        e,
      });
    });
    return out;
  }, [p.drawnEdges, p.layout]);

  const requestDraw = useCallback(() => {
    dirty.current = true;
  }, []);

  const clampCam = useCallback(() => {
    const c = cam.current;
    c.z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, c.z));
    const wrap = wrapRef.current;
    if (!wrap) return;
    const { layout } = propsRef.current;
    const vw = wrap.clientWidth / c.z;
    const vh = wrap.clientHeight / c.z;
    // Keep at least a sliver of graph on screen.
    c.x = Math.min(layout.width - 40, Math.max(-vw + 40, c.x));
    c.y = Math.min(layout.height - 40, Math.max(-vh + 40, c.y));
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const pr = propsRef.current;
    const { layout, palette, fontFamily } = pr;
    const dpr = window.devicePixelRatio || 1;
    const cw = wrap.clientWidth;
    const ch = wrap.clientHeight;
    // Backing-store dims are integers: round, or fractional DPR (1.25/1.5)
    // makes this comparison fail — and realloc+clear — on every frame.
    const bw = Math.round(cw * dpr);
    const bh = Math.round(ch * dpr);
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw;
      canvas.height = bh;
      canvas.style.width = `${cw}px`;
      canvas.style.height = `${ch}px`;
    }
    const c = cam.current;
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = palette.canvasBg;
    ctx.fillRect(0, 0, cw, ch);
    ctx.setTransform(dpr * c.z, 0, 0, dpr * c.z, -c.x * c.z * dpr, -c.y * c.z * dpr);

    const view = { x: c.x, y: c.y, width: cw / c.z, height: ch / c.z };
    const inView = (r: { x: number; y: number; width: number; height: number }) =>
      r.x < view.x + view.width && r.x + r.width > view.x &&
      r.y < view.y + view.height && r.y + r.height > view.y;

    // 1. Group boxes.
    for (const g of pr.viewGroups) {
      const b = layout.groupBounds.get(g.container.id);
      if (!b || !inView(b)) continue;
      const dim = pr.groupMatches && !pr.groupMatches.has(g.container.id);
      ctx.globalAlpha = dim ? 0.25 : 1;
      ctx.fillStyle = palette.groupBg;
      ctx.strokeStyle = palette.groupBorder;
      ctx.lineWidth = 1 / c.z;
      ctx.beginPath();
      pathRoundRect(ctx, b.x, b.y, b.width, b.height, 6);
      ctx.fill();
      ctx.stroke();
      if (c.z >= LOD_HEADER) {
        ctx.fillStyle = palette.headerText;
        ctx.font = `600 ${CAPTION_PX}px ${fontFamily}`;
        ctx.textBaseline = 'top';
        const label = `${g.container.label ?? g.container.id} · ${g.members.length}`;
        ctx.fillText(fitText(ctx, label, b.width - 16), b.x + 8, b.y + 5);
      }
    }
    ctx.globalAlpha = 1;

    // 2. Edges, batched per family so stroke state changes rarely.
    const byFam = new Map<RelationFamily, typeof edgeGeom>();
    for (const g of edgeGeom) {
      if (pr.hiddenFamilies.has(g.fam) || !inView(g.bb)) continue;
      const list = byFam.get(g.fam) ?? [];
      list.push(g);
      byFam.set(g.fam, list);
    }
    byFam.forEach((list, fam) => {
      const style = pr.palette.family[fam];
      ctx.strokeStyle = style.stroke;
      ctx.lineWidth = style.width / Math.max(c.z, 0.5);
      ctx.setLineDash(style.dash.map(d => d / Math.max(c.z, 0.5)));
      for (const g of list) {
        // Aggregated directory edges carry a count: thickness says how
        // heavy the coupling is (log steps so 10x reads as one step up).
        const count = g.e.count;
        if (count) {
          const boost = count >= 1000 ? 3.2 : count >= 100 ? 2.2 : count >= 10 ? 1.5 : 1;
          ctx.lineWidth = (style.width * boost) / Math.max(c.z, 0.5);
        }
        const dim =
          pr.matches &&
          !pr.matches.has(String(g.e.source)) &&
          !pr.matches.has(String(g.e.target));
        ctx.globalAlpha = dim ? 0.15 : 1;
        ctx.beginPath();
        ctx.moveTo(g.pts[0].x, g.pts[0].y);
        for (let i = 1; i < g.pts.length; i++) ctx.lineTo(g.pts[i].x, g.pts[i].y);
        ctx.stroke();
      }
    });
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    // 3. Nodes.
    for (const n of pr.leafNodes) {
      const pos = layout.positions.get(n.id);
      const size = pr.nodeSizes.get(n.id);
      if (!pos || !size) continue;
      const rect = { x: pos.x, y: pos.y, width: size.width, height: size.height };
      if (!inView(rect)) continue;
      const active = pr.activeIds.includes(n.id) || hoverId.current === n.id;
      const dim = pr.matches && !pr.matches.has(n.id) && !active;
      ctx.globalAlpha = dim ? 0.25 : 1;
      ctx.fillStyle = palette.nodeBg;
      ctx.strokeStyle = active ? palette.ring : palette.nodeBorder;
      ctx.lineWidth = (active ? 2 : 1) / c.z;
      ctx.beginPath();
      pathRoundRect(ctx, rect.x, rect.y, rect.width, rect.height, 4);
      ctx.fill();
      ctx.stroke();
      if (c.z >= LOD_LABEL) {
        const isDir = n._dir === true;
        const kind = isDir ? 'directory' : symbolKind(n);
        const { px, weight } = pr.bucketFont(n.id);
        ctx.textBaseline = 'top';
        ctx.font = `600 ${CAPTION_PX}px ${fontFamily}`;
        ctx.fillStyle = isDir ? palette.headerText : palette.kind[kind] ?? palette.headerText;
        ctx.fillText(fitText(ctx, kind, rect.width - 16), rect.x + 8, rect.y + 6);
        ctx.font = `${weight} ${px}px ${fontFamily}`;
        ctx.fillStyle = palette.labelText;
        ctx.fillText(
          fitText(ctx, n.label ?? n.id, rect.width - 16),
          rect.x + 8,
          rect.y + 6 + Math.round(CAPTION_PX * LINE),
        );
      }
    }
    ctx.globalAlpha = 1;
  }, [edgeGeom]);

  // rAF loop: draw only when dirty. The next frame is scheduled BEFORE
  // draw() runs so a throwing frame can never kill the loop (a frozen canvas
  // with live-looking controls is the worst failure mode this surface has);
  // the first error of a streak is surfaced via onDrawError, then muted.
  useEffect(() => {
    const loop = () => {
      raf.current = requestAnimationFrame(loop);
      if (dirty.current) {
        dirty.current = false;
        try {
          draw();
          drawErrored.current = false;
        } catch (e) {
          if (!drawErrored.current) {
            drawErrored.current = true;
            propsRef.current.onDrawError?.(String((e as Error).message ?? e));
          }
        }
      }
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);

  // Redraw on any prop change (matches, toggles, theme, layout, selection) —
  // and re-arm error reporting, so a Retry that runs into a persistent draw
  // failure re-surfaces the banner instead of failing muted.
  useEffect(() => {
    drawErrored.current = false;
    requestDraw();
  });

  const setZoomAnchored = useCallback(
    (nz: number, ax: number, ay: number) => {
      const c = cam.current;
      const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, nz));
      // Keep the world point under the anchor fixed.
      const wx = c.x + ax / c.z;
      const wy = c.y + ay / c.z;
      c.z = z;
      c.x = wx - ax / z;
      c.y = wy - ay / z;
      clampCam();
      propsRef.current.onZoomChange(c.z);
      requestDraw();
    },
    [clampCam, requestDraw],
  );

  const glideTo = useCallback(
    (tx: number, ty: number, tz: number) => {
      cancelAnimationFrame(anim.current);
      const c = cam.current;
      if (propsRef.current.prefersReducedMotion) {
        c.x = tx; c.y = ty; c.z = tz;
        clampCam();
        propsRef.current.onZoomChange(c.z);
        requestDraw();
        return;
      }
      const from = { ...c };
      const t0 = performance.now();
      const D = 260;
      const step = (now: number) => {
        const t = Math.min(1, (now - t0) / D);
        const k = 1 - (1 - t) ** 3;
        c.x = from.x + (tx - from.x) * k;
        c.y = from.y + (ty - from.y) * k;
        c.z = from.z + (tz - from.z) * k;
        propsRef.current.onZoomChange(c.z);
        requestDraw();
        if (t < 1) anim.current = requestAnimationFrame(step);
      };
      anim.current = requestAnimationFrame(step);
    },
    [clampCam, requestDraw],
  );

  // Camera API for the toolbar / search / table / detail panel.
  useEffect(() => {
    const api: CameraApi = {
      getZoom: () => cam.current.z,
      zoomTo: z => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        setZoomAnchored(z, wrap.clientWidth / 2, wrap.clientHeight / 2);
      },
      zoomBy: f => api.zoomTo(cam.current.z * f),
      fit: () => {
        const wrap = wrapRef.current;
        const { layout } = propsRef.current;
        if (!wrap) return;
        const z = Math.min(
          ZOOM_MAX,
          Math.max(
            ZOOM_MIN,
            Math.min(
              (wrap.clientWidth - 24) / layout.width,
              (wrap.clientHeight - 24) / layout.height,
            ),
          ),
        );
        glideTo(
          -(wrap.clientWidth / z - layout.width) / 2,
          -(wrap.clientHeight / z - layout.height) / 2,
          z,
        );
      },
      centerOn: (id, minZoom) => {
        const wrap = wrapRef.current;
        const pr = propsRef.current;
        const pos = pr.layout.positions.get(id);
        const size = pr.nodeSizes.get(id);
        // Containers live in groupBounds, not positions — fall back so a
        // jump to a file/group node still centers instead of no-opping.
        const rect =
          pos && size
            ? { x: pos.x, y: pos.y, width: size.width, height: size.height }
            : pr.layout.groupBounds.get(id);
        if (!wrap || !rect) return;
        const z = Math.max(cam.current.z, minZoom ?? 0);
        glideTo(
          rect.x + rect.width / 2 - wrap.clientWidth / 2 / z,
          rect.y + rect.height / 2 - wrap.clientHeight / 2 / z,
          z,
        );
      },
    };
    props.cameraRef.current = api;
    return () => {
      props.cameraRef.current = null;
    };
  }, [glideTo, setZoomAnchored, props.cameraRef]);

  // First layout for a graph: fit oversized graphs, else 100% with margin.
  useEffect(() => {
    cam.current.fitted = false;
  }, [p.layout]);
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || cam.current.fitted) return;
    cam.current.fitted = true;
    const fitsAt1 =
      p.layout.width <= wrap.clientWidth && p.layout.height <= wrap.clientHeight;
    if (fitsAt1) {
      cam.current.x = -20;
      cam.current.y = -20;
      cam.current.z = 1;
      p.onZoomChange(1);
      requestDraw();
    } else {
      props.cameraRef.current?.fit();
    }
  }, [p.layout, p.onZoomChange, props.cameraRef, requestDraw, p]);

  const hitTest = useCallback((sx: number, sy: number): KgNode | null => {
    const c = cam.current;
    const wx = c.x + sx / c.z;
    const wy = c.y + sy / c.z;
    const pr = propsRef.current;
    for (const n of pr.leafNodes) {
      const pos = pr.layout.positions.get(n.id);
      const size = pr.nodeSizes.get(n.id);
      if (!pos || !size) continue;
      if (wx >= pos.x && wx <= pos.x + size.width && wy >= pos.y && wy <= pos.y + size.height) {
        return n;
      }
    }
    return null;
  }, []);

  // Pointer interactions: drag = pan, small movement = click/select, hover =
  // tooltip. Wheel zooms anchored at the cursor (trackpad pinch arrives as
  // ctrl+wheel).
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    let downAt: { x: number; y: number } | null = null;
    let moved = false;

    const local = (ev: PointerEvent | WheelEvent) => {
      const r = wrap.getBoundingClientRect();
      return { x: ev.clientX - r.left, y: ev.clientY - r.top };
    };

    const onDown = (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      downAt = local(ev);
      moved = false;
      try {
        wrap.setPointerCapture(ev.pointerId);
      } catch {
        /* pointer already released (touch cancel, synthetic events) */
      }
    };
    const onMove = (ev: PointerEvent) => {
      const pos = local(ev);
      if (downAt) {
        const dx = pos.x - downAt.x;
        const dy = pos.y - downAt.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
        if (moved) {
          cancelAnimationFrame(anim.current);
          cam.current.x -= dx / cam.current.z;
          cam.current.y -= dy / cam.current.z;
          clampCam();
          downAt = pos;
          wrap.style.cursor = 'grabbing';
          requestDraw();
        }
        return;
      }
      const hit = hitTest(pos.x, pos.y);
      const tip = tooltipRef.current;
      if (hit?.id !== hoverId.current) {
        hoverId.current = hit?.id ?? null;
        wrap.style.cursor = hit ? 'pointer' : 'grab';
        requestDraw();
      }
      if (tip) {
        if (hit) {
          tip.textContent = nodeTitle(hit, propsRef.current.degree.get(hit.id) ?? 0);
          tip.style.display = 'block';
          tip.style.left = `${Math.min(pos.x + 14, wrap.clientWidth - 280)}px`;
          tip.style.top = `${pos.y + 14}px`;
        } else {
          tip.style.display = 'none';
        }
      }
    };
    const onUp = (ev: PointerEvent) => {
      if (downAt && !moved) {
        const pos = local(ev);
        const hit = hitTest(pos.x, pos.y);
        propsRef.current.onSelect(hit?.id ?? null);
      }
      downAt = null;
      wrap.style.cursor = hoverId.current ? 'pointer' : 'grab';
    };
    const onLeave = () => {
      hoverId.current = null;
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
      requestDraw();
    };
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      cancelAnimationFrame(anim.current);
      const pos = local(ev);
      const factor = Math.exp(-ev.deltaY * (ev.ctrlKey ? 0.01 : 0.0018));
      setZoomAnchored(cam.current.z * factor, pos.x, pos.y);
    };

    wrap.addEventListener('pointerdown', onDown);
    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerup', onUp);
    wrap.addEventListener('pointerleave', onLeave);
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      wrap.removeEventListener('pointerdown', onDown);
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerup', onUp);
      wrap.removeEventListener('pointerleave', onLeave);
      wrap.removeEventListener('wheel', onWheel);
    };
  }, [clampCam, hitTest, requestDraw, setZoomAnchored]);

  // Resize keeps the canvas crisp.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const ro = new ResizeObserver(requestDraw);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [requestDraw]);

  return (
    // Keyboard: arrows pan, +/- zoom, 0 fits, Escape clears selection.
    // The canvas is paint-only; the table view is the accessible reading.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={wrapRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      role="region"
      aria-label={p.regionLabel}
      style={{
        position: 'relative',
        height: p.height,
        overflow: 'hidden',
        borderRadius: 4,
        cursor: 'grab',
        outline: 'none',
      }}
      onKeyDown={ev => {
        const c = cam.current;
        const pan = 80 / c.z;
        if (ev.key === 'ArrowLeft') c.x -= pan;
        else if (ev.key === 'ArrowRight') c.x += pan;
        else if (ev.key === 'ArrowUp') c.y -= pan;
        else if (ev.key === 'ArrowDown') c.y += pan;
        else if (ev.key === '+' || ev.key === '=') props.cameraRef.current?.zoomBy(1.25);
        else if (ev.key === '-') props.cameraRef.current?.zoomBy(1 / 1.25);
        else if (ev.key === '0') props.cameraRef.current?.fit();
        else if (ev.key === 'Escape') propsRef.current.onSelect(null);
        else return;
        ev.preventDefault();
        clampCam();
        requestDraw();
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      <div
        ref={tooltipRef}
        aria-hidden
        style={{
          display: 'none',
          position: 'absolute',
          maxWidth: 280,
          padding: '4px 8px',
          fontSize: 11,
          lineHeight: 1.35,
          borderRadius: 4,
          pointerEvents: 'none',
          background: p.palette.nodeBg,
          color: p.palette.labelText,
          border: `1px solid ${p.palette.nodeBorder}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          zIndex: 3,
          wordBreak: 'break-all',
        }}
      />
    </div>
  );
};
